import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Image from 'components/global/Image';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';

import { faXmark } from '@fortawesome/pro-solid-svg-icons';

import Button from './Button';
import FileInput from './FileInput';
import * as S from './styled/ImageInput.styled';

const ImageModal = NiceModal.create(({ value, loading }) => {
	const modal = useModal();

	return (
		<Modal maxWidth="small" className="modal-imageInput">
			<Image
				onClick={() => modal.remove()}
				url={value}
				width="100%"
				height="100%"
				style={{
					display: loading ? 'none' : 'inline-block',

					paddingTop: '3rem'
				}}
			/>
		</Modal>
	);
});
/**
 * @author @buunsal
 * @description image input to allow user upload image
 * @param {Object} props - props of the component.
 * @param {String} [props.placeholder] - placeholder of the input field.
 * @param {String} [props.label] - label of the input field.
 * @param {Boolean} [props.disabled=false] - disabled state of the input field.
 * @param {Boolean} [props.error=false] - error state of the input field.
 * @param {String} [props.errorMessage] - error message of the input field.
 * @param {String} props.name - name of the input field.
 * @param {Boolean} [props.required=false] - required state of the input field.
 * @param {Boolean} [props.viewMode=false] - viewMode state of the input field.
 * @returns {JSX} StringInput component.
 * @example
 * <ImageInput
 * label="image"
 * disabled={false}
 * name="name"
 * required={true}
 * />
 */
const ImageInput = ({
	value,
	disabled,
	setValue,
	name,
	defaultUrl,
	placeholder,
	viewMode,
	className,
	acceptedFileType = 'image/*,.pdf',
	imageAlt = 'uploadedImage',
	...rest
}) => {
	const [defaultImage, setDefaultImage] = useState(defaultUrl);
	const { t } = useTranslation();
	const onRemoveFile = () => {
		setValue(null);
		setDefaultImage(null);
		setErrorMessage(null);
	};
	const [errorMessage, setErrorMessage] = useState('');

	const afterError = () => {
		if (acceptedFileType.includes('pdf')) {
			return;
		}
		return setErrorMessage(defaultUrl ? 'noImageUploaded' : 'errorImage');
	};

	return (
		<div>
			<div className={className}>
				{value ? (
					<S.FlexRow>
						<Image
							afterError={afterError}
							defaultUrl={defaultImage}
							url={value}
							alt={imageAlt}
							height="5rem"
							onClick={() => NiceModal.show(ImageModal, { value })}
						/>

						{!viewMode && (
							<>
								<Typography className="fileName" align="center">
									{errorMessage ? errorMessage : <a href={value}>{value}</a>}
								</Typography>
								<Button variant="borderless" icon={faXmark} onClick={() => onRemoveFile()} iconButton />
							</>
						)}
					</S.FlexRow>
				) : viewMode ? (
					<Typography>-</Typography>
				) : (
					<S.InputContainer disabled={disabled}>
						<div>{!value && placeholder && <span id="placeholder">{t(placeholder)}</span>}</div>
						<S.FlexRow>
							<FileInput
								onRemoveFile={onRemoveFile}
								name={name}
								disabled={disabled}
								style={{ width: '100%' }}
								acceptedFileType={acceptedFileType}
								setValue={setValue}
								maxFileSize={1000000}
								{...rest}
							/>
						</S.FlexRow>
					</S.InputContainer>
				)}
			</div>
		</div>
	);
};

export default ImageInput;
